import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { Button, Contact, StatusBadge, colors, toast, useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import { AddressCard, CompactPreview } from './styles';
import { AccountAutocomplete, AccountOption } from './AccountAutocomplete';
import { normalizePartyName } from '../../../utils/partyNameUtils';

const MY_ACCOUNTS_MATCH_QUERY = gql`
  query MyAccountsMatch($accountType: String!, $search: String, $limit: Int) {
    myAccounts(accountType: $accountType, search: $search, limit: $limit) {
      items { id name email phone country }
    }
  }
`;

const ADD_TRADE_PARTNER_ACCOUNT_MUTATION = gql`
  mutation AddTradePartnerAccountFromImport($input: AddTradePartnerAccountInput!) {
    addTradePartnerAccount(input: $input) {
      success
      message
    }
  }
`;

interface ExtractedParty {
  name: string;
  email: string;
  phone?: string;
  addressLine1?: string;
  city?: string;
  country?: string;
}

interface PartyMatchCardProps {
  title: string;
  accountType: 'vendor' | 'customer';
  fieldPrefix: 'Vendor' | 'Customer';
  extracted: ExtractedParty;
  /** True when this side is the caller's own pinned identity — the backend
   * ignores whatever name/email was extracted for this side and always
   * resolves it to the caller's own account (see order_service.create_order),
   * so the extracted text is never the real answer here. */
  isSelf: boolean;
  selfIdentity?: { name: string; email?: string };
  onResolved: (fields: Record<string, string>) => void;
  /** Fires whenever this card's resolved/unresolved status changes, so the
   * wizard can block order creation until every party has either been
   * confirmed as-is or the user has explicitly said what to do about a
   * mismatch — extraction picking the wrong party (a signatory's name, the
   * platform's own letterhead) must never silently become the order's
   * counterparty just because *some* text was present. */
  onResolutionChange: (resolved: boolean) => void;
}

const norm = normalizePartyName;

export const PartyMatchCard: React.FC<PartyMatchCardProps> = ({
  title,
  accountType,
  fieldPrefix,
  extracted,
  isSelf,
  selfIdentity,
  onResolved,
  onResolutionChange,
}) => {
  const nameKey = `${fieldPrefix}Name`;
  const emailKey = `${fieldPrefix}Email`;
  const phoneKey = `${fieldPrefix}ContactNumber`;
  const countryKey = fieldPrefix === 'Vendor' ? 'vendorCountry' : 'customerCountry';

  // For a name-mismatch-on-email-match, or a fully unmatched party: the user
  // must actively choose what to do. Until they do, this card is unresolved
  // and the wizard's final submit stays blocked.
  const [mismatchAcknowledged, setMismatchAcknowledged] = useState(false);
  // "No, it's a different X" on a mismatch — the found match is real, the
  // user is just saying it isn't who this party actually is, so treat this
  // card as if no match had been found at all (falls through to the
  // search-or-add-new state below).
  const [matchRejected, setMatchRejected] = useState(false);
  const [manualPick, setManualPick] = useState<AccountOption | null>(null);
  const [addedAsNew, setAddedAsNew] = useState(false);

  useEffect(() => {
    if (!isSelf || !selfIdentity) return;
    // Only back-fill when extraction found *nothing* for this field —
    // overwriting a genuinely different extracted value here (e.g. the
    // invoice's real consignee, "GRUPA WEDU Sp z.o.o") would erase it from
    // `extracted` before the mismatch check below ever runs, silently
    // hiding exactly the discrepancy this card exists to surface. The
    // backend pins this side to the caller's own identity regardless of
    // what's submitted, so leaving a genuinely-extracted name/email in
    // place costs nothing beyond display.
    const fields: Record<string, string> = {};
    if (selfIdentity.name && !extracted.name) fields[nameKey] = selfIdentity.name;
    if (selfIdentity.email && !extracted.email) fields[emailKey] = selfIdentity.email;
    if (Object.keys(fields).length > 0) onResolved(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf, selfIdentity?.name, selfIdentity?.email]);

  // Two independent lookups, not one search-by-"email-or-name" query — a
  // single query keyed on extracted.email (when present) can never surface a
  // roster row that shares the typed name under a *different* email, because
  // the backend's search only matches the one term it's given (see
  // myAccounts's search_clause). Without the name-keyed query below, a
  // second "Tvikla Private Limited" under a new email sails straight through
  // as "New — not on your roster" with no warning at all.
  const { data: byEmailData, previousData: byEmailPrev, loading: emailLoading } = useGraphQLQuery<{ myAccounts: { items: AccountOption[] } }>(MY_ACCOUNTS_MATCH_QUERY, {
    showErrorToast: false,
    variables: { accountType, search: extracted.email || null, limit: 5 },
    skip: isSelf || !extracted.email,
  });
  // The backend does a plain LOWER(name) LIKE %search% substring match — it
  // has no idea "M/S." is an honorific to ignore, so searching on the raw
  // extracted text ("M/S. GJA GRANITES") never finds a roster row saved as
  // plain "GJA GRANITES". Send the same normalized form used for the local
  // comparison below, so the two agree on what counts as "the same name."
  const normalizedName = norm(extracted.name);
  const { data: byNameData, previousData: byNamePrev, loading: nameLoading, refetch: refetchByEmail } = useGraphQLQuery<{ myAccounts: { items: AccountOption[] } }>(MY_ACCOUNTS_MATCH_QUERY, {
    showErrorToast: false,
    variables: { accountType, search: normalizedName || null, limit: 5 },
    skip: isSelf || !normalizedName,
  });
  // extracted.email/.name feed these queries' own variables, so every
  // keystroke into the editable name/email fields below re-runs them and
  // flips `loading` back to true. Falling back to `previousData` (Apollo
  // keeps the prior result around across a variables-driven refetch) instead
  // of clearing `items` to [] on every keystroke keeps this card on the same
  // render branch while typing — without it, the whole card would swap to
  // the "Checking…" placeholder and back on every character, unmounting the
  // input the user is actively typing into and dropping focus mid-word.
  const resultData = byEmailData ?? byEmailPrev;
  const nameResultData = byNameData ?? byNamePrev;
  const loading = emailLoading || nameLoading;
  const hasAnyResult = !!(resultData || nameResultData);
  // Set when "Add as new" turns out to have matched an existing account by
  // email under a different name than what was typed — the backend matches
  // purely by email and silently returns the existing row unchanged (see
  // order_repository.get_or_create_vendor), so without this check "Add as
  // new" can silently re-attach to a stranger's account while reporting
  // success, which is exactly the silent-substitution problem this whole
  // card exists to prevent.
  const [emailCollision, setEmailCollision] = useState<AccountOption | null>(null);

  const itemsById = new Map<string | number, AccountOption>();
  for (const item of [...(resultData?.myAccounts.items ?? []), ...(nameResultData?.myAccounts.items ?? [])]) {
    itemsById.set(item.id, item);
  }
  const items = Array.from(itemsById.values());
  const matchByEmail = items.find((item) => !!extracted.email && item.email?.toLowerCase() === extracted.email.toLowerCase());
  const matchByName = items.find((item) => !!extracted.name && norm(item.name) === norm(extracted.name));
  const foundMatch = matchByEmail || matchByName;
  const match = matchRejected ? undefined : foundMatch;
  // Which field disagrees with what's on file, if any — email is checked
  // first because the backend treats it as the authoritative match key (see
  // order_repository.get_or_create_vendor), so a same-email/different-name
  // row (e.g. an invoice's letterhead reading "Tvikla Private Limited" for
  // an email that's actually "GJA GRANITES") is a name conflict. Otherwise,
  // if only the name matched under a *different* email, that's a genuine
  // possible duplicate of an existing roster entry and needs the same
  // explicit confirmation, not a silent second account under the new email.
  const mismatchField: 'name' | 'email' | null = matchByEmail
    ? (extracted.name && norm(matchByEmail.name) !== norm(extracted.name) ? 'name' : null)
    : (matchByName && extracted.email && matchByName.email && norm(matchByName.email) !== norm(extracted.email) ? 'email' : null);
  const nameMismatch = !!(match && mismatchField);

  const [addAccount, { loading: adding }] = useGraphQLMutation(ADD_TRADE_PARTNER_ACCOUNT_MUTATION, {
    showSuccessToast: false,
    showErrorToast: false,
  });

  const resolvedMatch = match && (!nameMismatch || mismatchAcknowledged) ? match : manualPick;

  useEffect(() => {
    if (isSelf || !resolvedMatch) return;
    // Name is always taken from the roster — that's the whole point of
    // confirming a match. But phone/email/country are per-shipment details
    // that legitimately vary invoice to invoice (a different branch office,
    // an updated number) — only back-fill them from the roster when *this*
    // invoice didn't extract its own, rather than clobbering a genuinely
    // extracted Indian phone/address with a stale value saved on the
    // roster row from an unrelated earlier order.
    onResolved({
      [nameKey]: resolvedMatch.name,
      ...(resolvedMatch.email && !extracted.email ? { [emailKey]: resolvedMatch.email } : {}),
      ...(resolvedMatch.phone && !extracted.phone ? { [phoneKey]: resolvedMatch.phone } : {}),
      ...(resolvedMatch.country && !extracted.country ? { [countryKey]: resolvedMatch.country } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf, resolvedMatch?.id]);

  const isResolved = isSelf || !!resolvedMatch || addedAsNew;
  useEffect(() => {
    onResolutionChange(isResolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResolved]);

  const handleAddAsNew = async () => {
    const email = extracted.email.trim();
    try {
      const response = await addAccount({
        variables: {
          input: {
            accountType,
            name: extracted.name.trim(),
            email,
            country: extracted.country || undefined,
          },
        },
      });
      const result = response.data?.addTradePartnerAccount;
      if (!result?.success) {
        toast({ title: "Couldn't add account", description: result?.message, variant: 'destructive' });
        return;
      }
      // The mutation reports success even when it matched an existing
      // account by email rather than creating a new one (see comment on
      // emailCollision above) — re-check the roster for that email so a
      // collision under a different name gets caught here instead of just
      // silently doing nothing while claiming "added as new."
      const refetched = await refetchByEmail({ accountType, search: email, limit: 5 });
      const collision = refetched.data?.myAccounts.items.find(
        (item) => item.email?.toLowerCase() === email.toLowerCase() && norm(item.name) !== norm(extracted.name)
      );
      if (collision) {
        setEmailCollision(collision);
        return;
      }
      toast({ title: `Added as new ${accountType === 'vendor' ? 'supplier' : 'customer'}`, description: result?.message });
      setAddedAsNew(true);
    } catch (err) {
      toast({
        title: "Couldn't add account",
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const cardStyle = { display: 'flex', flexDirection: 'column' as const, gap: '1rem', gridColumn: 'span 1' };
  const headerStyle = { display: 'flex', justifyContent: 'space-between' as const, alignItems: 'center' as const };
  const titleStyle = { fontSize: '14px', fontWeight: 600, color: '#475569' };

  if (isSelf) {
    const name = selfIdentity?.name || extracted.name;
    const email = selfIdentity?.email || extracted.email;
    // Compare unconditionally, not just when extracted.name/.email happen to
    // be non-empty — "nothing was extracted for this party" vs. "your real
    // account has a name and email on file" is exactly as much of a
    // mismatch as two different non-empty strings, and silently passed as
    // "Confirmed" with no note before this fix.
    const selfMismatch = !!(
      selfIdentity &&
      (norm(extracted.name || '') !== norm(selfIdentity.name || '') ||
        norm(extracted.email || '') !== norm(selfIdentity.email || ''))
    );
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="success" label={selfMismatch ? 'Confirmed — your account' : 'Confirmed'} />
        </div>
        <CompactPreview>
          <strong>{name || 'Name Missing'}</strong>
          <p>{extracted.addressLine1}</p>
          <p>{[extracted.city, extracted.country].filter(Boolean).join(', ')}</p>
          <p>{email}</p>
        </CompactPreview>
        {selfMismatch && (
          <div style={{ fontSize: 12, color: colors.gray600, background: colors.gray100, borderRadius: 8, padding: '8px 10px' }}>
            {extracted.name || extracted.email ? (
              <>This document lists this party as <strong>{extracted.name || extracted.email}</strong>, but this order</>
            ) : (
              <>No name or email was found for this party on the document, but this order</>
            )}{' '}
            is always created under your own account — every order you create binds to <strong>{name}</strong> regardless
            of what a source document says.
          </div>
        )}
      </AddressCard>
    );
  }

  if (loading && !hasAnyResult) {
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="neutral" label="Checking…" />
        </div>
        <CompactPreview>Checking your roster for a match…</CompactPreview>
      </AddressCard>
    );
  }

  if (emailCollision) {
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="pending" label="Needs your confirmation" />
        </div>
        <div style={{ fontSize: 13, color: colors.gray700, lineHeight: 1.5 }}>
          That email is already on file for <strong>{emailCollision.name}</strong> — nothing new was added. If that's
          not who this is, use a different email address below.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" onClick={() => { setManualPick(emailCollision); setEmailCollision(null); }}>
            Yes, this is {emailCollision.name}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEmailCollision(null)}>
            Use a different email
          </Button>
        </div>
      </AddressCard>
    );
  }

  if (match && nameMismatch && !mismatchAcknowledged) {
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="pending" label="Needs your confirmation" />
        </div>
        <div style={{ fontSize: 13, color: colors.gray700, lineHeight: 1.5 }}>
          {mismatchField === 'email' ? (
            <>You already have <strong>{match.name}</strong> on file under a different email ({match.email}) than the one on
            this document ({extracted.email}). Is this the same {accountType === 'vendor' ? 'supplier' : 'customer'}?</>
          ) : (
            <>This document names this party <strong>{extracted.name}</strong>, but that email ({match.email}) is already
            on file for <strong>{match.name}</strong> in your accounts.</>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" onClick={() => setMismatchAcknowledged(true)}>
            Yes, this is {match.name}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setMatchRejected(true)}>
            No, it's a different {accountType === 'vendor' ? 'supplier' : 'customer'}
          </Button>
        </div>
      </AddressCard>
    );
  }

  if (resolvedMatch) {
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="success" label="Confirmed" />
        </div>
        <CompactPreview>
          <strong>{resolvedMatch.name}</strong>
          <p>{extracted.addressLine1}</p>
          <p>{[extracted.city, extracted.country || resolvedMatch.country].filter(Boolean).join(', ')}</p>
          <p>{resolvedMatch.email}</p>
        </CompactPreview>
      </AddressCard>
    );
  }

  if (addedAsNew) {
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="success" label="Added as new" />
        </div>
        <CompactPreview>
          <strong>{extracted.name || 'Name Missing'}</strong>
          <p>{extracted.addressLine1}</p>
          <p>{[extracted.city, extracted.country].filter(Boolean).join(', ')}</p>
          <p>{extracted.email}</p>
        </CompactPreview>
      </AddressCard>
    );
  }

  return (
    <AddressCard style={cardStyle}>
      <div style={headerStyle}>
        <h4 style={titleStyle}>{title}</h4>
        <StatusBadge size="sm" variant="pending" label="New — not on your roster" />
      </div>
      <AccountAutocomplete
        accountType={accountType}
        label={`${fieldPrefix} Name`}
        name={nameKey}
        value={extracted.name}
        onChange={(e) => onResolved({ [nameKey]: e.target.value })}
        onAccountSelected={(account: AccountOption) => setManualPick(account)}
        placeholder="Search your roster for who this really is"
      />
      <Contact
        variant="email"
        label={`${fieldPrefix} Email`}
        value={extracted.email}
        onChange={(e) => onResolved({ [emailKey]: e.target.value })}
      />
      <div style={{ fontSize: 13, color: colors.gray700 }}>
        This doesn't match anyone on your roster. Is this a{' '}
        {accountType === 'vendor' ? 'supplier' : 'customer'} you haven't worked with before?
      </div>
      <div>
        <Button size="sm" onClick={handleAddAsNew} disabled={adding || !extracted.name.trim() || !extracted.email.trim()}>
          {adding ? 'Adding…' : `Yes, add as new ${accountType === 'vendor' ? 'supplier' : 'customer'}`}
        </Button>
      </div>
    </AddressCard>
  );
};
